import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { SourceOption } from '../_lib/source-option'
import { SourceGlyph } from './source-glyph'
import { SourceMark } from './source-mark'
import { SourceCountBadge, SourceOptionButton } from './source-option-button'

/**
 * Close grace period for mouse travel: the options sit `sideOffset` below the
 * mark, and an instant close on `pointerleave` would collapse the stack while
 * the pointer crosses that gap.
 */
const HOVER_CLOSE_DELAY_MS = 150

/**
 * The source mark on a tray or schedule card, upgraded into a switcher
 * (LG-362 §2): at rest it shows the source the card currently wears plus a
 * count badge for the *other* places the problem is listed; hovering, tapping
 * or activating it fans those out, and clicking one reassigns the card to
 * that source — the same `setSource` write the card-details modal makes,
 * without opening the modal.
 *
 * The fan-out is a portaled Radix Popover, not an in-tree absolute overlay:
 * both hosts clip descendants (the week section is `overflow-hidden`, the
 * tray list `overflow-y-auto`), so an in-tree drop-down is cut off at the
 * bottom card of a column and pads the tray with phantom scroll space even
 * while hidden. Portaling costs the CSS-only reveal, so JS owns the whole
 * visibility decision here — including the cases `reveal-on-hover.ts` warns
 * a hover-only gate silently drops: the trigger is a real button, so touch
 * taps it open and keyboard opens it with focus moved into the options;
 * mouse keeps the hover fan-out via the pointer handlers (`pointerType`
 * gated so a tap does not open-then-toggle-shut).
 *
 * The semantics are the card-details `SourceSwitcher`'s doctrine, kept here:
 * the current source is the front mark and never a target — writing back the
 * source a card already wears would be a no-op that reads as a broken button.
 *
 * The degrade rule is this component's, not the callers': it falls back to
 * the plain static mark when there is nothing to switch — one option
 * (nowhere else to point), none (a coding exercise or one-off card, which
 * has no per-source identity — `fallbackIcon` still shows the icon the card
 * resolved server-side), or no `onPick` (a read-only board, a closed card).
 * Callers pass their data verbatim; emptying `sources` to mean "not
 * permitted" would conflate two different states. `fallbackLabel` names the
 * source on that static mark where nothing else on the card does — without
 * it the mark stays decorative, for hosts whose own accessible name already
 * carries the source.
 */
export function InlineSourceSwitcher({
  sources,
  currentSourceId,
  fallbackIcon = null,
  fallbackLabel = null,
  onPick,
}: {
  /** Owning first — the reader's sort is the contract, not re-sorted here. */
  sources: readonly SourceOption[]
  /** `TrayCard.sourceId`: the explicit pick, null = the owning source. */
  currentSourceId: string | null
  /** The card's resolved icon, shown when there are no options to offer. */
  fallbackIcon?: string | null
  /** Names the source on the degraded static mark; null keeps it decorative. */
  fallbackLabel?: string | null
  /**
   * Resolves when the write settles; failures are the caller's to report.
   * Absent means no write is available here — the mark stays static.
   */
  onPick?: (sourceId: string) => Promise<unknown>
}): ReactNode {
  // Same resolution chain as the card-details SourceSwitcher: an explicit
  // pick, else the owning option (null never matches a real sourceId), else
  // whatever the reader put first.
  // `.at(0)`, not `[0]`: with noUncheckedIndexedAccess off, `[0]` would type
  // `current` as always-present and make the undefined guard below look dead
  // to the checker — while an empty `sources` (any one-off card) makes it
  // load-bearing at runtime.
  const current =
    sources.find((option) => option.sourceId === currentSourceId) ??
    sources.find((option) => option.owning) ??
    sources.at(0)
  const others = sources.filter(
    (option) => option.sourceId !== current?.sourceId,
  )

  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const closeTimer = useRef<number | null>(null)
  // True while the popover owes its open state to hover alone — then opening
  // must not steal focus into the options, and closing must not yank it back
  // to the trigger from wherever the user actually is.
  const hoverOpened = useRef(false)

  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    },
    [],
  )

  if (current === undefined || others.length === 0 || onPick === undefined) {
    return (
      <SourceMark
        icon={current?.icon ?? fallbackIcon}
        label={fallbackLabel}
        decorative={fallbackLabel == null}
      />
    )
  }

  const cancelClose = (): void => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  // Gated on `pointerType === 'mouse'`: a tap fires pointerenter before
  // click, and an ungated hover-open would make that click toggle the
  // popover straight shut again.
  const hoverEnter = (event: ReactPointerEvent): void => {
    if (event.pointerType !== 'mouse') return
    cancelClose()
    if (!open) hoverOpened.current = true
    setOpen(true)
  }
  const hoverLeave = (event: ReactPointerEvent): void => {
    if (event.pointerType !== 'mouse') return
    // Hover may only close what hover opened. A keyboard user's idle cursor
    // drifting off the trigger must not dismiss the popover they opened with
    // Enter mid-navigation — deliberate opens already have Escape, outside
    // click, and the trigger toggle for dismissal.
    if (!hoverOpened.current) return
    cancelClose()
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null
      setOpen(false)
    }, HOVER_CLOSE_DELAY_MS)
  }

  const handlePick = async (sourceId: string): Promise<void> => {
    if (saving) return
    setSaving(true)
    try {
      await onPick(sourceId)
    } finally {
      setSaving(false)
      // A pick is a deliberate interaction inside the popover, however it
      // was opened — clearing the hover flag lets Radix's close restore
      // focus to the trigger. Without this, a mouse pick leaves focus on
      // document.body (the clicked option is disabled mid-save, which
      // ejects focus) and the next Tab restarts from the page top.
      hoverOpened.current = false
      setOpen(false)
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Radix only fires this for real interactions — trigger click/Enter,
        // Escape, outside dismissal — so any open from here is a deliberate
        // one that focus management should treat normally.
        cancelClose()
        if (next) hoverOpened.current = false
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Switch source — currently ${current.displayName}, ${others.length} other option${others.length === 1 ? '' : 's'}`}
          className="relative block shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
          onPointerEnter={hoverEnter}
          onPointerLeave={hoverLeave}
          onClick={(event) => {
            // A mouse click always lands with the fan-out already
            // hover-opened; letting Radix toggle would flash it shut the
            // moment it appeared. `preventDefault` skips Radix's toggle
            // (its composed handler checks `defaultPrevented`) without
            // touching the tap and keyboard paths, where no hover-open
            // precedes the click.
            if (open && hoverOpened.current) event.preventDefault()
          }}
        >
          <SourceGlyph displayName={current.displayName} icon={current.icon} />
          <SourceCountBadge
            count={others.length}
            className={open ? 'opacity-0' : ''}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="center"
        sideOffset={2}
        onPointerEnter={hoverEnter}
        onPointerLeave={hoverLeave}
        onOpenAutoFocus={(event) => {
          if (hoverOpened.current) event.preventDefault()
        }}
        onCloseAutoFocus={(event) => {
          if (hoverOpened.current) {
            hoverOpened.current = false
            event.preventDefault()
          }
        }}
        className="flex w-auto min-w-0 flex-col items-center gap-1 rounded-md border border-(--cd-border-2) bg-(--cd-card) p-1 shadow-md"
      >
        {others.map((option) => {
          const label =
            option.externalId === null
              ? `Assign from ${option.displayName}`
              : `Assign from ${option.displayName} · ${option.externalId}`
          return (
            <SourceOptionButton
              key={option.sourceId}
              option={option}
              label={label}
              tooltip={`${label} instead`}
              disabled={saving}
              onClick={() => void handlePick(option.sourceId)}
            />
          )
        })}
      </PopoverContent>
    </Popover>
  )
}
